

'use client';

import { useState, useEffect, useRef, useMemo, type CSSProperties } from 'react';
import Image from '@/components/ui/Image';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Shield, Users, Crown, Calendar, Info, ShieldCheck, Hourglass, UserPlus, Trash2, Check, X, Newspaper, ImageIcon, Edit, Copy, Send, Link as LinkIcon, Handshake, Heart, Palette, Church, FileSignature, AlignLeft, Star, Pin, Settings, PlusCircle, BookOpen, Mail, FileText } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { requestToJoinHermandad, approveJoinRequest, rejectJoinRequest, removeBrother, createSocialPost, deleteSocialPost, createSocialStory, inviteToCollaborate, acceptCollaboration, rejectCollaboration, toggleLikePost, saveHermandadSettings, createContract, signContract, rejectContract, deleteSocialStory, getArchivedStories, createHighlightStoryGroup, deleteHighlightStoryGroup, sendCarta } from '@/lib/actions';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetClose } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { JartinetHermandad, UserProfile, JoinRequest, SocialPost, SocialStory, Banda, Contract, Organizacion, HighlightStoryGroup, PapeletaDeSitio, DiaSS } from "@/lib/types";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '@/lib/utils';
import { useMemoFirebase, useAuth } from '@/firebase/index';
import { db } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { useDoc } from '@/firebase/firestore/use-doc';
import { ContractCard } from '../contratos/ContractCard';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { doc, collection, query, where, orderBy } from 'firebase/firestore';
import { Checkbox } from '../ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PrimitivaIcon } from '@/components/icons/PrimitivaIcon';
import { PontificiaIcon } from '@/components/icons/PontificiaIcon';
import { PapeletaDeSitioPreview } from './PapeletaDeSitio';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { HermandadDetail } from './HermandadDetail';
import { idsDelDia, posicionDelDia } from '@/lib/programa-ss';

export const dynamic = 'force-dynamic';

// El orden litúrgico de los días y los ids de cada uno viven en `@/lib/programa-ss`,
// compartidos con el libro del programa y con el panel de admin.

function ListadoRealDocument({ hermandades }: { hermandades: JartinetHermandad[] }) {
    const ranking = useMemo(() => {
        return hermandades.map(h => ({
            ...h,
            totalHermanos: (Array.isArray(h.hermanos) ? h.hermanos.length : 0) + (Array.isArray(h.hermanosMayores) ? h.hermanosMayores.length : 0)
        })).sort((a,b) => b.totalHermanos - a.totalHermanos).slice(0, 10);
    }, [hermandades]);

    return (
        <DialogContent className="max-w-xl overflow-hidden rounded-2xl border border-white/10 bg-card p-0">
            <div className="relative overflow-hidden px-6 pt-8 pb-6 text-center">
                <div className="absolute -top-20 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-primary/15 blur-[100px]" />
                <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary to-amber-500" />
                <div className="relative mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10">
                    <Crown className="h-6 w-6 text-primary" />
                </div>
                <DialogHeader className="relative p-0">
                    <DialogTitle className="font-headline text-2xl font-bold">
                        <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">Listado Real</span>
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground">
                        Ranking de hermandades por número de hermanos
                    </DialogDescription>
                </DialogHeader>
            </div>
            <ScrollArea className="max-h-[55vh] px-6 pb-2">
                <div className="space-y-2">
                    {ranking.map((h, index) => (
                        <div key={h.id} className="flex items-center gap-3 rounded-xl bg-muted/40 p-3">
                            <span className={cn(
                                "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full font-headline text-sm font-bold",
                                index === 0
                                    ? "bg-gradient-to-br from-primary to-amber-500 text-primary-foreground shadow-[0_0_10px_rgba(250,204,21,0.4)]"
                                    : "bg-background/60 text-muted-foreground"
                            )}>
                                {index + 1}
                            </span>
                            <img src={h.escudoUrl} alt={h.nombre} className="h-9 w-9 flex-shrink-0 object-contain" />
                            <p className="flex-1 truncate text-sm font-semibold">{h.nombreCompleto}</p>
                            <span className="flex-shrink-0 whitespace-nowrap text-xs font-medium text-primary">{h.totalHermanos} hermanos</span>
                        </div>
                    ))}
                </div>
            </ScrollArea>
            <DialogFooter className="border-t border-white/5 p-4">
                <DialogClose asChild>
                    <Button variant="outline" className="w-full">Cerrar</Button>
                </DialogClose>
            </DialogFooter>
        </DialogContent>
    )
}

interface HermandadesClientProps {
    initialHermandades: JartinetHermandad[];
    initialUserProfiles: Record<string, UserProfile>;
    initialRequests: Record<string, JoinRequest[]>;
    notifications?: Record<string, number>;
    initialStories: Record<string, SocialStory[]>;
    /** Días del programa de Semana Santa; `null` mientras cargan. */
    diasSS?: DiaSS[] | null;
    allPosts: SocialPost[];
    currentUser: { uid: string } | null;
    allOrganizaciones: Organizacion[];
    allBandas: Banda[];
    onAction: () => void;
}

export function HermandadesClient({
    initialHermandades,
    initialUserProfiles,
    initialRequests,
    notifications = {},
    initialStories,
    diasSS = null,
    allPosts,
    currentUser,
    allOrganizaciones,
    allBandas,
    onAction
}: HermandadesClientProps) {
  const [selectedHermandad, setSelectedHermandad] = useState<JartinetHermandad | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  
  const handleHermandadClick = (hermandad: JartinetHermandad) => {
    setSelectedHermandad(hermandad);
    setIsSheetOpen(true);
  };
  
  const hexToRgba = (hex: string, alpha: number) => {
    if (!hex || hex.length < 7) hex = '#000000'; // Fallback for invalid hex
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getPostsForHermandad = (hermandadId: string) => {
    return allPosts.filter(post => post.authorId === hermandadId && post.authorType === 'hermandad');
  };

  const getStoriesForHermandad = (hermandadId: string) => {
      const key = `hermandad:${hermandadId}`;
      return initialStories[key] || [];
  };
  
  const hermandadesDePenitencia = initialHermandades.filter(h => h.tipo === 'Hermandad');
  const hermandadesDeGloria = initialHermandades.filter(h => h.tipo === 'Hermandad de Gloria');
  const proHermandades = initialHermandades.filter(h => h.tipo === 'Pro-Hermandad');
  const gruposParroquiales = initialHermandades.filter(h => h.tipo === 'Grupo Parroquial');

  // Agrupación por día de salida. La fuente de verdad es el PROGRAMA DE SEMANA SANTA
  // (`dias_ss`): el día de una hermandad es aquel en cuyo array está metida, en orden
  // litúrgico (`posicionDelDia`) y respetando el orden de las hermandades dentro del día.
  // Mientras el programa carga se usa el campo `dia` de la hermandad para no dejar la
  // lista en blanco; una vez cargado, manda el programa.
  const { grupos: hermandadesPorDia, sinDia: hermandadesSinDia } = (() => {
    const grupos: { dia: string; hermandades: JartinetHermandad[] }[] = [];

    if (!diasSS) {
      // Programa aún sin cargar: agrupación provisional por el campo `dia`.
      const porNombre = new Map<string, JartinetHermandad[]>();
      for (const h of hermandadesDePenitencia) {
        if (!h.dia) continue;
        if (!porNombre.has(h.dia)) porNombre.set(h.dia, []);
        porNombre.get(h.dia)!.push(h);
      }
      for (const [dia, hermandades] of porNombre) grupos.push({ dia, hermandades });
      return { grupos, sinDia: hermandadesDePenitencia.filter(h => !h.dia) };
    }

    const porId = new Map(hermandadesDePenitencia.map(h => [h.id, h]));
    const yaColocadas = new Set<string>();

    for (const dia of [...diasSS].sort((a, b) => posicionDelDia(a) - posicionDelDia(b))) {
      const hermandades: JartinetHermandad[] = [];
      for (const id of idsDelDia(dia)) {
        const h = porId.get(id);
        // Solo penitencia, y nunca dos veces (una hermandad podría estar duplicada
        // en el programa si se ha manipulado a mano).
        if (!h || yaColocadas.has(id)) continue;
        yaColocadas.add(id);
        hermandades.push(h);
      }
      if (hermandades.length > 0) grupos.push({ dia: dia.nombredia, hermandades });
    }

    return { grupos, sinDia: hermandadesDePenitencia.filter(h => !yaColocadas.has(h.id)) };
  })();

  // Un día con una sola hermandad no merece una línea entera: se pega a la del día
  // anterior. Si es el primero de todos (no hay anterior), es el día siguiente el que
  // se pega a él. Cada fila reparte el ancho en proporción al nº de hermandades.
  const filasDeDias = (() => {
    const filas: { dia: string; hermandades: JartinetHermandad[] }[][] = [];
    for (const grupo of hermandadesPorDia) {
      const filaAnterior = filas[filas.length - 1];
      const esUnico = grupo.hermandades.length === 1;
      const anteriorHuerfana = !!filaAnterior && filaAnterior.every(g => g.hermandades.length === 1);
      if (filaAnterior && (esUnico || anteriorHuerfana)) {
        filaAnterior.push(grupo);
      } else {
        filas.push([grupo]);
      }
    }
    return filas;
  })();

  // `columnas` solo se pasa cuando el grupo comparte línea con otro día: fija el nº de
  // columnas de su rejilla (de `md` para arriba) para que sus tarjetas quepan en una fila.
  const renderHermandadGroup = (
    title: string,
    hermandades: JartinetHermandad[],
    variant: 'section' | 'day' = 'section',
    columnas?: number
  ) => {

    const getHeaderImage = (hermandad: JartinetHermandad) => {
        return hermandad.foto || "https://i.imgur.com/kYkL52G.png";
    };

    const isDay = variant === 'day';

    return (
     <div key={title} className="animate-in fade-in slide-in-from-bottom-4 duration-500">
        {isDay ? (
            <h3 className="font-headline text-xl font-semibold text-foreground/90 mb-4 flex items-center gap-3">
                <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary/70" />
                {title}
                <span className="text-[0.7rem] font-normal text-muted-foreground/70">
                    {hermandades.length} {hermandades.length === 1 ? 'hermandad' : 'hermandades'}
                </span>
                <span className="h-px flex-1 bg-gradient-to-r from-white/15 to-transparent rounded-full" />
            </h3>
        ) : (
            <h2 className="font-headline text-3xl font-bold text-primary mb-6 flex items-center gap-4">
                {title}
                <span className="h-[2px] flex-1 bg-gradient-to-r from-primary/50 to-transparent rounded-full"></span>
            </h2>
        )}
        <div
            className={cn(
                'grid gap-5 grid-cols-1 sm:grid-cols-2',
                columnas
                    ? 'md:[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]'
                    : 'md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
            )}
            style={columnas ? ({ '--cols': columnas } as CSSProperties) : undefined}
        >
          {hermandades.map(hermandad => {
              const isPrimitiva = hermandad.titulos?.includes('primitiva');
              const isPontificia = hermandad.titulos?.includes('pontificia');
              const memberCount = (hermandad.hermanos?.length || 0) + (hermandad.hermanosMayores?.length || 0);
              const color = hermandad.colorPrimario || '#9b8854';
              const colorSec = hermandad.colorSecundario || color;
              const notifCount = notifications[hermandad.id] || 0;
            return (
            <motion.div
                key={hermandad.id}
                whileHover={{ y: -6 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleHermandadClick(hermandad)}
                className="group relative cursor-pointer"
                transition={{ type: 'spring', stiffness: 300, damping: 22 }}
            >
                <div className="relative overflow-hidden rounded-2xl border border-white/8 h-full flex flex-col transition-all duration-300 shadow-md group-hover:shadow-[0_8px_32px_rgba(0,0,0,0.35)] group-hover:border-white/20"
                    style={{ background: `linear-gradient(145deg, ${hexToRgba(color, 0.18)} 0%, hsl(var(--card)) 55%)` }}
                >
                    {/* Top accent bar */}
                    <div
                        className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ background: `linear-gradient(90deg, ${color}, ${colorSec})` }}
                    />

                    {/* Background photo with overlay */}
                    <div className="absolute inset-0 z-0 overflow-hidden rounded-2xl">
                        <div
                            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-110 opacity-15 group-hover:opacity-25"
                            style={{ backgroundImage: `url('${getHeaderImage(hermandad)}')` }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/60 to-transparent" />
                    </div>

                    {/* Notification badge — top right (solicitudes/contratos pendientes) */}
                    {notifCount > 0 && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="absolute top-2.5 right-2.5 z-20">
                                        <span className="relative flex items-center justify-center">
                                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-50" />
                                            <span className="relative inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[0.68rem] font-bold leading-none text-white shadow-lg ring-2 ring-background">
                                                {notifCount > 99 ? '99+' : notifCount}
                                            </span>
                                        </span>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{notifCount} {notifCount === 1 ? 'notificación pendiente' : 'notificaciones pendientes'}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}

                    {/* Title badges — top left */}
                    <div className="absolute top-3 left-3 flex gap-1.5 z-10">
                        {isPrimitiva && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="bg-background/75 backdrop-blur-md p-1.5 rounded-full border border-white/10 shadow-sm">
                                            <div className="h-3.5 w-3.5"><PrimitivaIcon /></div>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent><p>Primitiva Hermandad</p></TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        {isPontificia && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="bg-background/75 backdrop-blur-md p-1.5 rounded-full border border-white/10 shadow-sm">
                                            <div className="h-3.5 w-3.5"><PontificiaIcon /></div>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent><p>Hermandad Pontificia</p></TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>

                    {/* Main content */}
                    <div className="relative z-10 flex flex-col items-center p-5 pt-6 gap-3 flex-1">
                        {/* Shield with color glow */}
                        <div className="relative flex-shrink-0">
                            <div
                                className="absolute inset-0 blur-xl rounded-full scale-[1.8] opacity-0 group-hover:opacity-40 transition-opacity duration-500"
                                style={{ backgroundColor: color }}
                            />
                            <Image
                                src={hermandad.escudoUrl}
                                alt={`Escudo de ${hermandad.nombre}`}
                                width={76}
                                height={76}
                                className="h-[76px] w-[76px] object-contain relative z-10 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] transition-transform duration-400 group-hover:-translate-y-1"
                            />
                        </div>

                        {/* Name + sede */}
                        <div className="flex flex-col items-center text-center gap-0.5 flex-1 justify-center">
                            <h3 className="font-headline font-bold text-foreground group-hover:text-primary transition-colors duration-300 leading-tight text-[0.95rem] line-clamp-2">
                                {hermandad.nombre}
                            </h3>
                            {hermandad.sede && (
                                <p className="text-[0.7rem] text-muted-foreground/60 line-clamp-1 mt-0.5">
                                    {hermandad.sede}
                                </p>
                            )}
                        </div>

                        {/* Footer: member count + animated line */}
                        <div className="w-full flex flex-col items-center gap-2 mt-1">
                            <div
                                className="w-full h-px transition-opacity duration-300 opacity-20 group-hover:opacity-40"
                                style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
                            />
                            <div className="flex items-center gap-1.5 text-[0.7rem] text-muted-foreground/70">
                                <Users className="h-3 w-3 flex-shrink-0" />
                                <span>{memberCount} {memberCount === 1 ? 'hermano' : 'hermanos'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
          )})}
        </div>
      </div>
    );
  }


  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-headline text-5xl font-bold text-primary">
                Hermandades y Grupos
            </h1>
            <p className="mt-2 text-lg text-muted-foreground">
                Gestiona tu hermandad o únete a una.
            </p>
          </div>
          <Dialog>
              <DialogTrigger asChild>
                  <Button variant="outline" className="w-fit border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary">
                      <Crown className="h-4 w-4"/> Listado Real
                  </Button>
              </DialogTrigger>
              <ListadoRealDocument hermandades={initialHermandades} />
          </Dialog>
      </div>
      
      {initialHermandades.length > 0 ? (
        <div className="space-y-12">
            {hermandadesDePenitencia.length > 0 && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <h2 className="font-headline text-3xl font-bold text-primary mb-6 flex items-center gap-4">
                        Hermandades de Penitencia
                        <span className="h-[2px] flex-1 bg-gradient-to-r from-primary/50 to-transparent rounded-full"></span>
                    </h2>
                    <div className="space-y-9 border-l border-white/10 pl-4 sm:pl-6">
                        {filasDeDias.map(fila => (
                            <div
                                key={fila.map(g => g.dia).join('|')}
                                className={cn(
                                    'flex flex-col gap-9',
                                    fila.length > 1 && 'md:flex-row md:items-start md:gap-6'
                                )}
                            >
                                {fila.map(grupo => (
                                    <div
                                        key={grupo.dia}
                                        className="min-w-0"
                                        style={fila.length > 1 ? { flex: `${grupo.hermandades.length} 1 0%` } : undefined}
                                    >
                                        {renderHermandadGroup(
                                            grupo.dia,
                                            grupo.hermandades,
                                            'day',
                                            fila.length > 1 ? grupo.hermandades.length : undefined
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                        {hermandadesSinDia.length > 0 && renderHermandadGroup('Sin día asignado', hermandadesSinDia, 'day')}
                    </div>
                </div>
            )}
            {hermandadesDeGloria.length > 0 && renderHermandadGroup('Hermandades de Gloria', hermandadesDeGloria)}
            {proHermandades.length > 0 && renderHermandadGroup('Pro-Hermandades', proHermandades)}
            {gruposParroquiales.length > 0 && renderHermandadGroup('Grupos Parroquiales', gruposParroquiales)}
        </div>
      ) : (
         <div className="text-center py-16 border-2 border-dashed rounded-lg">
            <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-semibold">No hay hermandades</h3>
            <p className="mt-1 text-sm text-muted-foreground">Los datos de las hermandades no se pudieron cargar. Contacta con un administrador.</p>
        </div>
      )}

      <HermandadDetail 
        hermandad={selectedHermandad} 
        open={isSheetOpen} 
        onOpenChange={setIsSheetOpen} 
        requests={selectedHermandad ? initialRequests[selectedHermandad.id] || [] : []}
        allUserProfiles={initialUserProfiles}
        posts={selectedHermandad ? getPostsForHermandad(selectedHermandad.id) : []}
        stories={selectedHermandad ? getStoriesForHermandad(selectedHermandad.id) : []}
        allHermandades={initialHermandades}
        allBandas={allBandas}
        allPosts={allPosts}
        allOrganizaciones={allOrganizaciones}
        currentUser={currentUser}
        onAction={onAction}
      />
    </div>
  );
}
